import { getCrmColors } from "../../theme/crmColors";
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Avatar from "@material-ui/core/Avatar";
import Switch from "@material-ui/core/Switch";
import PermContactCalendarIcon from "@material-ui/icons/PermContactCalendar";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import { Alert } from "@material-ui/lab";
import CustomerFields from "../CustomerFields";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
// import AsyncSelect from "../AsyncSelect";
import QueueSelectSingle from "../QueueSelectSingle";
import { contactPresentation } from "../../pages/CrmRelationship/helpers";
import { AuthContext } from "../../context/Auth/AuthContext";
import { applyContactPhotoPatch } from "../../services/contactPhoto";
import useContactPhotoUpdates, { useIndividualContactPhoto } from "../../hooks/useContactPhotoUpdates";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		margin: 16,
		width: "calc(100% - 32px)",
		maxHeight: "calc(100% - 32px)",
		borderRadius: 8,
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		color: theme.mode === "light" ? "#172033" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
		boxShadow: theme.mode === "light" ? "0 24px 60px rgba(15, 23, 42, 0.18)" : "0 24px 60px rgba(0,0,0,0.42)",
		overflow: "hidden",
	},
	profileDialogPaper: { maxWidth: 880, borderRadius: 16 },
	dialogTitle: {
		flexShrink: 0,
		padding: theme.spacing(2, 2.5),
		background: theme.mode === "light" ? "linear-gradient(135deg, #ffffff 0%, #edf6ff 100%)" : (theme.ticketDark ? getCrmColors(theme, "dark").inset : "#343434"),
		borderBottom: theme.mode === "light" ? "1px solid #e6edf6" : (theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : "1px solid rgba(255,255,255,0.08)"),
	},
	titleRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
	},
	titleIcon: {
		width: 42,
		height: 42,
		borderRadius: 8,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").accentSoft : theme.palette.primary.main,
		color: "#ffffff",
		boxShadow: theme.mode === "light" ? "0 10px 22px rgba(93, 170, 252, 0.22)" : "0 10px 22px rgba(0,0,0,0.24)",
	},
	titleEyebrow: {
		fontSize: 11,
		fontWeight: 800,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: theme.mode === "light" ? "#315b8f" : "#9ccaef",
	},
	titleText: {
		fontSize: 22,
		lineHeight: 1.1,
		fontWeight: 900,
		color: theme.mode === "light" ? "#0f172a" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
	},
	form: {
		display: "flex",
		flexDirection: "column",
		flex: "1 1 auto",
		minHeight: 0,
		overflow: "hidden",
	},
	dialogContent: {
		flex: "1 1 auto",
		minHeight: 0,
		overflowY: "auto",
		padding: theme.spacing(2.25),
		background: theme.mode === "light" ? "#f4f7fb" : (theme.ticketDark ? getCrmColors(theme, "dark").background : "#424242"),
		borderTop: 0,
		borderBottom: 0,
		"& .MuiInputLabel-root": {
			color: theme.mode === "light" ? "#64748b" : (theme.ticketDark ? "#FFFFFF" : "#cbd5e1"),
		},
	},
	sectionBlock: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		borderRadius: 8,
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		border: theme.mode === "light" ? "1px solid #d9e2ef" : (theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : "1px solid rgba(255,255,255,0.08)"),
		boxShadow: theme.mode === "light" ? "0 8px 18px rgba(15, 23, 42, 0.04)" : "0 10px 24px rgba(0,0,0,0.14)",
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontSize: 13,
		fontWeight: 900,
		letterSpacing: 0.2,
		textTransform: "uppercase",
		color: theme.mode === "light" ? "#315b8f" : "#9ccaef",
	},
	fieldRow: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(1.25),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	recordLayout: {
		display: "grid",
		gridTemplateColumns: "208px minmax(0, 1fr)",
		alignItems: "start",
		gap: theme.spacing(2),
		[theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
	},
	recordDetails: { minWidth: 0 },
	photoPanel: {
		display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gridTemplateAreas: '"photo" "identity" "facts"', gap: theme.spacing(1.5), minWidth: 0,
		padding: theme.spacing(2), borderRadius: 12,
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		border: `1px solid ${theme.palette.divider}`,
		[theme.breakpoints.down("sm")]: { gridTemplateColumns: "84px minmax(0, 1fr)", gridTemplateAreas: '"photo identity" "facts facts"', alignItems: "center", padding: theme.spacing(1.5) },
	},
	profilePhoto: {
		width: 160, height: 160, flexShrink: 0, borderRadius: 12, gridArea: "photo", justifySelf: "center",
		background: theme.palette.action.hover, color: theme.palette.primary.main,
		"& svg": { fontSize: 80 },
		[theme.breakpoints.down("sm")]: { width: 84, height: 84, "& svg": { fontSize: 48 } },
	},
	profileIdentity: { gridArea: "identity", minWidth: 0, textAlign: "center", overflowWrap: "anywhere", [theme.breakpoints.down("sm")]: { textAlign: "left" } },
	profileName: { fontSize: 17, fontWeight: 800, lineHeight: 1.35, marginBottom: theme.spacing(.75) },
	profilePhone: { fontSize: 13, lineHeight: 1.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: theme.palette.primary.main },
	photoCaption: { fontSize: 11, fontWeight: 600, color: theme.palette.text.secondary, marginTop: theme.spacing(.75) },
	profileFacts: { gridArea: "facts", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: theme.spacing(1.5), margin: 0, paddingTop: theme.spacing(1.5), borderTop: `1px solid ${theme.palette.divider}`, minWidth: 0, [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" } },
	profileFact: { minWidth: 0, overflowWrap: "anywhere" },
	profileFactLabel: { fontSize: 11, fontWeight: 700, color: theme.palette.text.secondary, marginBottom: 3 },
	profileFactValue: { fontSize: 13, lineHeight: 1.45, fontWeight: 500, margin: 0 },
	textField: {
		flex: 1,
		marginTop: theme.spacing(0.75),
		marginBottom: theme.spacing(0.75),
		"& .MuiOutlinedInput-root": {
			borderRadius: 8,
			backgroundColor: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").inset : "#343434"),
			color: theme.mode === "light" ? "#172033" : (theme.ticketDark ? "#FFFFFF" : "#f8fafc"),
			"& fieldset": {
				borderColor: theme.mode === "light" ? "#d7e1ee" : (theme.ticketDark ? getCrmColors(theme, "dark").border : "rgba(255,255,255,0.12)"),
			},
			"&:hover fieldset": {
				borderColor: theme.mode === "light" ? "#c6d4e6" : "rgba(255,255,255,0.20)",
			},
			"&.Mui-focused fieldset": {
				borderColor: theme.palette.primary.main,
				borderWidth: 1,
				boxShadow: theme.mode === "light" ? "0 0 0 3px rgba(93, 170, 252, 0.12)" : "0 0 0 3px rgba(93, 170, 252, 0.14)",
			},
		},
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		gap: theme.spacing(1),
		[theme.breakpoints.down("xs")]: {
			alignItems: "stretch",
			flexDirection: "column",
		},
	},
	infoRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		color: theme.mode === "light" ? "#334155" : (theme.ticketDark ? "#FFFFFF" : "#dbeafe"),
		"& .MuiTypography-root": {
			fontSize: 13,
		},
	},
	addExtraButton: {
		flex: 1,
		marginTop: theme.spacing(1),
		borderRadius: 8,
		fontWeight: 800,
		borderColor: theme.mode === "light" ? "#bfdbfe" : "rgba(156,202,239,0.22)",
		backgroundColor: theme.mode === "light" ? "#edf6ff" : (theme.ticketDark ? getCrmColors(theme, "dark").selected : "#263f50"),
		color: theme.mode === "light" ? theme.palette.primary.main : (theme.ticketDark ? "#FFFFFF" : "#dbeafe"),
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#e5f2ff" : "#2e5065",
			borderColor: theme.palette.primary.main,
		},
	},
	dialogActions: {
		flexShrink: 0,
		padding: theme.spacing(1.5, 2.5),
		background: theme.mode === "light" ? "#ffffff" : (theme.ticketDark ? getCrmColors(theme, "dark").surface : "#2f2f2f"),
		borderTop: theme.mode === "light" ? "1px solid #e6edf6" : (theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : "1px solid rgba(255,255,255,0.08)"),
	},
	cancelButton: {
		borderRadius: 8,
		fontWeight: 800,
		textTransform: "uppercase",
		color: theme.mode === "light" ? "#64748b" : (theme.ticketDark ? "#FFFFFF" : "#dbeafe"),
		borderColor: theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.14)",
	},
	saveButton: {
		borderRadius: 8,
		fontWeight: 800,
		textTransform: "uppercase",
		boxShadow: theme.mode === "light" ? "0 10px 22px rgba(93, 170, 252, 0.22)" : "0 10px 22px rgba(0,0,0,0.24)",
	},
	deleteButton: {
		color: "#ff3860",
		backgroundColor: theme.mode === "light" ? "#fff6f8" : "rgba(255, 56, 96, 0.08)",
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#ffe8ee" : "rgba(255, 56, 96, 0.14)",
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModalForm = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);
	const saving = useRef(false);
	const fieldsEditor = useRef(null);
	const fieldsBusyRef = useRef(false);
	const crmConfirmed = useRef(false);
	const [fieldsReady, setFieldsReady] = useState(false);
	const [fieldsBusy, setFieldsBusy] = useState(false);
	const [fieldsRestricted, setFieldsRestricted] = useState(false);
	const [customerSummary, setCustomerSummary] = useState(null);
	const [contactReady, setContactReady] = useState(false);
	const [partialSave, setPartialSave] = useState(false);
	const fieldsStateChanged = useCallback(state => {
		fieldsBusyRef.current = state.busy;
		setFieldsBusy(state.busy);
		setFieldsReady(state.ready);
		setFieldsRestricted(state.restricted);
		setCustomerSummary(state.summary || null);
	}, []);

        const initialState = {
                name: "",
                number: "",
                email: "",
                disableBot: false,
                lgpdAcceptedAt: "",
                queueId: null,
                extraInfo: []
        };

	const [contact, setContact] = useState(initialState);
	// Photo events must not change Formik initialValues or reset unsaved fields.
	const [photoContact, setPhotoContact] = useState({ id: contactId });
	const { user = {}, socket } = useContext(AuthContext) || {};
	const mergePhotoSnapshot = useContactPhotoUpdates({ companyId: user.companyId, socket, enabled: open,
		onPatch: patch => setPhotoContact(current => applyContactPhotoPatch(current, patch)) });
	const onPhotoError = useIndividualContactPhoto({ companyId: user.companyId, contact: photoContact, enabled: open && contactReady });
	const [disableBot, setDisableBot] = useState(false);
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		let active = true;
		setContactReady(!contactId);
		setFieldsReady(false);
		setFieldsRestricted(false);
		setCustomerSummary(null);
		setFieldsBusy(false);
		fieldsBusyRef.current = false;
		setPartialSave(false);
		crmConfirmed.current = false;
		setContact(initialState);
		setDisableBot(false);
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
				setDisableBot(Boolean(initialValues.disableBot));
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (active && isMounted.current) {
					setContact({ ...data, extraInfo: (data.extraInfo || []).filter(info => !info.crmKey) });
					setPhotoContact(current => mergePhotoSnapshot(data, current));
					setDisableBot(data.disableBot)
					setContactReady(true);
				}
			} catch (err) {
				if (active) toastError(err);
			}
		};

		fetchContact();
		return () => { active = false; };
	}, [contactId, open, initialValues, mergePhotoSnapshot]);

	const closeContact = () => {
		fieldsBusyRef.current = false;
		setFieldsBusy(false);
		setContactReady(false);
		setCustomerSummary(null);
		onClose();
		setContact(initialState);
		setDisableBot(false);
	};
	const handleClose = () => {
		if (saving.current || fieldsBusyRef.current) return;
		closeContact();
	};

	const handleSaveContact = async values => {
		if (saving.current) return;
		saving.current = true;
		let completed = false;
		try {
			setPartialSave(false);
			const queueId = values.queueId || null;
			if (contactId) {
				if (!fieldsRestricted) {
					const changed = fieldsEditor.current?.hasChanges;
					const saved = await fieldsEditor.current?.save();
					if (!saved || !isMounted.current) return;
					if (changed) crmConfirmed.current = true;
				}
				await api.put(`/contacts/${contactId}`, {
					number: values.number, queueId, disableBot,
					extraInfo: (values.extraInfo || []).filter(info => !info.crmKey).map(info => ({ ...(info.id ? { id: info.id } : {}), name: info.name, value: info.value }))
				});
			} else {
				const { data } = await api.post("/contacts", { ...values, queueId, disableBot });
				if (isMounted.current && onSave) onSave(data);
			}
			if (!isMounted.current) return;
			toast.success(i18n.t("contactModal.success"));
			completed = true;
		} catch (err) {
			if (isMounted.current) {
				toastError(err);
				if (crmConfirmed.current) setPartialSave(true);
			}
		} finally {
			saving.current = false;
		}
		if (completed) closeContact();
	};
	const phoneField = ({ errors, touched, isSubmitting }) => <Field
		as={TextField} id="contact-phone-field" label={contactId ? "Telefone" : i18n.t("contactModal.form.number")} name="number"
		disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
		error={touched.number && Boolean(errors.number)} helperText={touched.number && errors.number}
		placeholder="5532999999999" variant="outlined" margin={contactId ? "none" : "dense"} size="small" fullWidth
		InputLabelProps={{ shrink: true }} className={contactId ? undefined : classes.textField}
	/>;
	const person = contactPresentation({ ...contact, name: customerSummary?.full_name || contact.name });
	const birthday = customerSummary?.birth_date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	const summaryFacts = customerSummary ? [
		["E-mail", customerSummary.email],
		["Empresa", customerSummary.organization],
		["Localização", [customerSummary["address.city_name"], customerSummary["address.state"]].filter(Boolean).join(" / ")],
		["Aniversário", birthday ? `${birthday[3]}/${birthday[2]}/${birthday[1]}` : ""]
	].filter(([, value]) => value) : [];

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={contactId ? "md" : "sm"}
				fullWidth
				scroll="paper"
				aria-labelledby="contact-modal-title"
				classes={{ paper: `${classes.dialogPaper} ${contactId ? classes.profileDialogPaper : ""}` }}
			>
				<DialogTitle id="contact-modal-title" disableTypography className={classes.dialogTitle}>
					<div className={classes.titleRow}>
						<div className={classes.titleIcon}>
							<PermContactCalendarIcon />
						</div>
						<div>
							<Typography className={classes.titleEyebrow}>
								Cadastro do contato
							</Typography>
							<Typography className={classes.titleText}>
								{contactId
									? "Ficha do cliente"
									: `${i18n.t("contactModal.title.add")}`}
							</Typography>
						</div>
					</div>
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={contactId ? Yup.object({ number: ContactSchema.fields.number }) : ContactSchema}
					onSubmit={handleSaveContact}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue, dirty }) => (
						<Form className={classes.form}>
							<DialogContent dividers className={classes.dialogContent}>
								{partialSave && <Alert severity="warning" role="alert">Dados do cliente salvos. Não foi possível salvar o atendimento e os campos antigos. Clique em Salvar para tentar novamente.</Alert>}
								<div className={contactId ? classes.recordLayout : undefined} data-testid={contactId ? "contact-profile-header" : undefined}>
								{contactId && <aside className={classes.photoPanel} aria-label="Resumo do cliente" data-testid="contact-profile-summary">
									<Avatar variant="rounded" className={classes.profilePhoto} data-testid="contact-profile-photo" src={photoContact.urlPicture || undefined} imgProps={{ onError: onPhotoError }} alt={`Foto de ${person.name}`}><PersonOutlineIcon /></Avatar>
									<div className={classes.profileIdentity}>
										<Typography component="p" className={classes.profileName} data-testid="contact-profile-summary-name">{person.name}</Typography>
										<Typography component="p" className={classes.profilePhone} data-testid="contact-profile-summary-phone">{person.phone || "Telefone não informado"}</Typography>
										<Typography className={classes.photoCaption}>Cadastro #{contactId}</Typography>
									</div>
									{summaryFacts.length > 0 && <dl className={classes.profileFacts} data-testid="contact-profile-summary-facts">
										{summaryFacts.map(([label, value]) => <div className={classes.profileFact} key={label}><Typography component="dt" className={classes.profileFactLabel}>{label}</Typography><Typography component="dd" className={classes.profileFactValue}>{value}</Typography></div>)}
									</dl>}
								</aside>}
								<div className={classes.recordDetails}>
								{contactId && <CustomerFields contactId={contactId} enabled={open && contactReady} embedded editorRef={fieldsEditor} disabled={isSubmitting}
									identityField={phoneField({ errors, touched, isSubmitting })}
									pendingChanges={dirty || disableBot !== Boolean(contact.disableBot)}
									onStateChange={fieldsStateChanged} onLegacyConverted={id => setFieldValue("extraInfo", (values.extraInfo || []).filter(info => Number(info.id) !== id))} />}
								{fieldsRestricted && <div className={classes.fieldRow}>
									<TextField label="Nome" variant="outlined" size="small" value={contact.name || ""} InputProps={{ readOnly: true }} />
									{phoneField({ errors, touched, isSubmitting })}
									<TextField label="E-mail" variant="outlined" size="small" value={contact.email || ""} InputProps={{ readOnly: true }} />
								</div>}
								<section className={classes.sectionBlock} aria-label={contactId ? "Atendimento" : "Identificação"}>
								<Typography className={classes.sectionTitle}>
									{contactId ? "Atendimento" : i18n.t("contactModal.form.mainInfo")}
								</Typography>
								{!contactId && <Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									fullWidth
									className={classes.textField}
								/>}
								{!contactId && phoneField({ errors, touched, isSubmitting })}
								{!contactId && <div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
										className={classes.textField}
									/>
								</div>}
								<div>
                                                                <TagsContainer contact={contact} className={classes.textField} />
                                                                </div>
                                                                <div>
                                                                        <QueueSelectSingle />
                                                                </div>
								<Typography className={classes.infoRow}>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() =>
											setDisableBot(!disableBot)
										}
										name="disableBot"
										disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
										color="primary"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								<Typography className={classes.infoRow}>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography className={classes.infoRow}>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : ""}
								</Typography>
								</section>
								{/* <Typography variant="subtitle1" gutterBottom>{i18n.t("contactModal.form.customer_portfolio")}</Typography> */}
								{/* <div style={{ marginTop: 10 }}>
									<AsyncSelect url="/users" dictKey={"users"}
										initialValue={values.user} width="100%" label={i18n.t("contactModal.form.attendant")}
										onChange={(event, value) => setFieldValue("userId", value ? value.id : null)} />
								</div>
								<div style={{ marginTop: 10 }}>
									<AsyncSelect url="/queue" dictKey={null}
										initialValue={values.queue} width="100%" label={i18n.t("contactModal.form.queue")}
										onChange={(event, value) => setFieldValue("queueId", value ? value.id : null)} />
								</div> */}
								<section className={classes.sectionBlock} aria-label="Outras informações">
								<Typography className={classes.sectionTitle}>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															aria-label={`Remover campo ${info.name || index + 1}`}
															size="small"
															onClick={() => remove(index)}
															disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
															className={classes.deleteButton}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													className={classes.addExtraButton}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
													disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && !contactReady)}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
								</section>
								</div>
								</div>
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									type="button"
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting || fieldsBusy}
									variant="outlined"
									className={classes.cancelButton}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting || fieldsBusy || (Boolean(contactId) && (!contactReady || (!fieldsReady && !fieldsRestricted)))}
									variant="contained"
									className={`${classes.btnWrapper} ${classes.saveButton}`}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

// Each opening/contact owns its form and pending responses.
const ContactModal = props => <ContactModalForm key={`${Boolean(props.open)}:${props.contactId || "new"}`} {...props} />;

export default ContactModal;
